import axios from '@/core/axios'
import { Cart, Order } from '@medusajs/medusa'
import { LiteProductVariant } from '@ortros/stoux-lib'
import { HttpResponse } from 'nexus-res'
import { Product } from 'stoux-shared-types/models/product'

import { AddLineItemDto } from '../dtos/add-line-item.dto'
import { AddShippingMethodForCartDto } from '../dtos/add-shipping-method-for-cart.dto'
import { CompleteCartDto } from '../dtos/complete-cart.dto'
import { CreateCartDto } from '../dtos/create-cart.dto'
import { CreatePaymentSessionDto } from '../dtos/create-payment-session.dto'
import { DeleteLineItemDto } from '../dtos/delete-line-item.dto'
import { GetCartDto } from '../dtos/get-cart.dto'
import { GetVariantByIdDto } from '../dtos/get-variant-by-id.dto'
import { StoreOption } from '../dtos/list-shipping-options-by-cart.dto'
import { SetPaymentSessionDto } from '../dtos/set-payment-session.dto'
import { UpdateCartDto } from '../dtos/update-cart.dto'
import { UpdateLineItemDto } from '../dtos/update-line-item.dto'
import { CartRepository } from '../repositories/cart'
import { AddLineItemSchema } from '../schemas/add-line-item.schema'
import { AddShippingMethodForCartSchema } from '../schemas/add-shipping-method-for-cart.schema'
import { CompleteCartSchema } from '../schemas/complete-cart.schema'
import { CreateCartSchema } from '../schemas/create-cart.schema'
import { CreatePaymentSessionSchema } from '../schemas/create-payment-session.schema'
import { DeleteLineItemSchema } from '../schemas/delete-line-item.schema'
import { GetCartSchema } from '../schemas/get-cart.schema'
import { GetVariantByIdSchema } from '../schemas/get-variant-by-id.schema'
import { SetPaymentSessionSchema } from '../schemas/set-payment-session.schema'
import { UpdateCartSchema } from '../schemas/update-cart.schema'
import { UpdateLineItemSchema } from '../schemas/update-line-item.schema'
import { DiscountRepository } from '../../Discount/repositories/discount'

type InjectedDependencies = {
    cartRepository: CartRepository
    discountRepository: DiscountRepository
}

export class CartService {
    private readonly _cartRepository: CartRepository
    private readonly _discountRepository: DiscountRepository

    constructor({ cartRepository, discountRepository }: InjectedDependencies) {
        this._cartRepository = cartRepository
        this._discountRepository = discountRepository
    }

    /**
     * This will create a local cart only
     */
    async createCart(dto: CreateCartDto): Promise<Cart> {
        const validatedDto = await CreateCartSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the cart creation.')
        }

        return await this._cartRepository.create(validatedDto.data)
    }

    async getCartById(dto: GetCartDto): Promise<Cart> {
        const validatedDto = await GetCartSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the cart retrieval.')
        }

        return await this._cartRepository.getById(validatedDto.data)
    }

    async updateCart(dto: UpdateCartDto): Promise<Cart> {
        const validatedDto = await UpdateCartSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the cart update.')
        }

        return await this._cartRepository.update(validatedDto.data)
    }

    // * Line-items

    async addLineItem(dto: AddLineItemDto, currentCart?: Cart): Promise<Cart> {
        const validatedDto = await AddLineItemSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the line-item addition.')
        }

        // Si le panier a un code promo appliqué, le retirer avant d'ajouter l'article
        const cart = currentCart
        if (cart?.discounts?.length) {
            const discountCode = cart.discounts[0].code
            try {
                await this._discountRepository.remove({
                    cart_id: dto.cart_id,
                    code: discountCode,
                })
            } catch {
                // Si la suppression échoue, on continue quand même
            }
        }

        return await this._cartRepository.addLineItem(validatedDto.data)
    }

    async updateLineItem(dto: UpdateLineItemDto): Promise<Cart> {
        const validatedDto = await UpdateLineItemSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the line-item update.')
        }

        return await this._cartRepository.updateLineItem(validatedDto.data)
    }

    async deleteLineItem(dto: DeleteLineItemDto): Promise<Cart> {
        const validatedDto = await DeleteLineItemSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the line-item deletion.')
        }

        return await this._cartRepository.deleteLineItem(validatedDto.data)
    }

    async getVariantById(
        dto: GetVariantByIdDto,
    ): Promise<
        HttpResponse<LiteProductVariant & { template_barcode: string }>
    > {
        const validatedDto = await GetVariantByIdSchema.safeParseAsync(dto)

        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the variant retrieval.')
        }

        return await this._cartRepository.getVariantById(dto)
    }

    async getVariantByTemplateId(id: string): Promise<Product> {
        const { data } = await axios.post<{ product: Product }>(
            `/variants/${id}/cart`,
        )
        return data.product
    }

    async addShippingMethodForCart(
        dto: AddShippingMethodForCartDto,
    ): Promise<Cart> {
        const validatedDto =
            await AddShippingMethodForCartSchema.safeParseAsync(dto)

        if (!validatedDto.success) {
            throw new Error(
                'Invalid DTO provided for the shipping method addition.',
            )
        }

        return await this._cartRepository.addShippingMethodForCart(
            validatedDto.data,
        )
    }

    async createPaymentSession(dto: CreatePaymentSessionDto): Promise<Cart> {
        const validatedDto =
            await CreatePaymentSessionSchema.safeParseAsync(dto)
        if (!validatedDto.success) {
            throw new Error(
                'Invalid DTO provided for the payment session creation.',
            )
        }

        return await this._cartRepository.createPaymentSession(
            validatedDto.data,
        )
    }

    async setPaymentSession(dto: SetPaymentSessionDto): Promise<Cart> {
        const validatedDto = await SetPaymentSessionSchema.safeParseAsync(dto)

        if (!validatedDto.success) {
            throw new Error(
                'Invalid DTO provided for the payment session setting.',
            )
        }

        return await this._cartRepository.setPaymentSession(validatedDto.data)
    }

    async complete(dto: CompleteCartDto): Promise<Order | Cart> {
        const validatedDto = await CompleteCartSchema.safeParseAsync(dto)

        if (!validatedDto.success) {
            throw new Error('Invalid DTO provided for the cart completion.')
        }

        return await this._cartRepository.complete(dto)
    }

    async listShippingOptionsByCart(dto: GetCartDto): Promise<StoreOption[]> {
        const validated = await GetCartSchema.safeParseAsync(dto)

        if (!validated.success) {
            throw new Error(
                'Invalid DTO provided for the shipping options retrieval.',
            )
        }

        return await this._cartRepository.listShippingOptionsByCart(
            validated.data,
        )
    }

    async adjustCartAmount(dto: GetCartDto): Promise<Cart> {
        const validated = await GetCartSchema.safeParseAsync(dto)

        if (!validated.success) {
            throw new Error('Invalid DTO provided for the cart adjustment.')
        }

        return await this._cartRepository.adjustCartAmount(validated.data)
    }
}
